import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(num: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(num);
}

/**
 * Gets a proxied image URL for use with Next.js Image component
 * @param url The original external image URL
 * @returns The proxied URL through our API
 */
export function getProxiedImageUrl(url: string | null | undefined): string {
  if (!url) return "";

  // If the URL is already a data URL, return it as is
  if (url.startsWith("data:")) return url;

  // If the URL is a relative URL or from our own domain, return it as is
  if (
    url.startsWith("/") ||
    url.startsWith(process.env.NEXT_PUBLIC_APP_URL || "")
  )
    return url;

  // Otherwise, use the proxy
  const encodedUrl = encodeURIComponent(url);
  return `/api/image-proxy?url=${encodedUrl}`;
}

// Function to strip markdown formatting and keep only the important text
export const stripMarkdown = (text?: string): string => {
  if (!text) return "";
  let cleanedText = text;
  // Remove common nonsense phrases from web scraping
  const nonsensePhrases = [
    "Skip to content",
    "Skip to main content",
    "Skip navigation",
    "Jump to navigation",
    "Jump to search",
    "Main menu",
    "Secondary menu",
    "Sign up for our newsletter",
    "Accept cookies",
    "This site uses cookies",
  ];
  nonsensePhrases.forEach((phrase) => {
    cleanedText = cleanedText.replace(new RegExp(phrase, "gi"), "");
  });

  return (
    cleanedText
      // Remove markdown links [text](url) -> text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      // Remove reference-style links [text][ref] -> text
      .replace(/\[([^\]]+)\]\[[^\]]*\]/g, "$1")
      // Remove images ![alt](url) -> alt (or empty if no alt)
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
      // Remove bold/italic **text** or *text* -> text
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      // Remove strikethrough ~~text~~ -> text
      .replace(/~~([^~]+)~~/g, "$1")
      // Remove inline code `code` -> code
      .replace(/`([^`]+)`/g, "$1")
      // Remove headers ### text -> text
      .replace(/^#{1,6}\s+/gm, "")
      // Remove blockquotes > text -> text
      .replace(/^>\s+/gm, "")
      // Remove horizontal rules --- or ***
      .replace(/^[-*]{3,}$/gm, "")
      // Remove list markers - item or * item or 1. item -> item
      .replace(/^[\s]*[-*+]\s+/gm, "")
      .replace(/^[\s]*\d+\.\s+/gm, "")
      // Remove extra whitespace and line breaks
      .replace(/\n+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
};

export function processSourceMaterialResponse(text?: string): string {
  if (!text) return "";

  // Remove a leading markdown heading like "### Overview" (case-insensitive)
  return text.replace(/^\s*#{1,6}\s*Overview\s*:?[ \t]*\n*/i, "").trimStart();
}

export function normalizeAsciiCitations(text: string): string {
  let normalized = text
    .replace(/[【［]([^】］]+)[】］]/g, (_, inner: string) => {
      const cleaned = inner.split(/[†A-Za-z]/)[0] ?? "";
      const nums = cleaned.match(/\d+/g) ?? [];
      return nums.length > 0 ? `[${nums.join(", ")}]` : inner;
    })
    .replace(/\[\[\s*([0-9]+(?:\s*[,，]\s*[0-9]+)*)\s*\]\]/g, (_, nums: string) => {
      const cleaned = nums
        .split(/[,，]/)
        .map((n) => n.trim())
        .filter(Boolean)
        .join(", ");
      return `[${cleaned}]`;
    })
    .replace(/【\s*([0-9]+(?:\s*[,，]\s*[0-9]+)*)\s*】/g, (_, nums: string) => {
      const cleaned = nums
        .split(/[,，]/)
        .map((n) => n.trim())
        .filter(Boolean)
        .join(", ");
      return `[${cleaned}]`;
    })
    .replace(/\[\s*([0-9]+(?:\s*[,，]\s*[0-9]+)*)\s*\]/g, (_, nums: string) => {
      const cleaned = nums
        .split(/[,，]/)
        .map((n) => n.trim())
        .filter(Boolean)
        .join(", ");
      return `[${cleaned}]`;
    });

  // Merge adjacent citations like [1][2] -> [1, 2]
  normalized = normalized.replace(/\[([0-9,\s]+)\]\s*\[([0-9,\s]+)\]/g, (_, a, b) => {
    const merged = `${a}, ${b}`
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean)
      .join(", ");
    return `[${merged}]`;
  });

  return normalized;
}

/**
 * Process text and replace source citations [1] [2] etc. with proper markdown links
 * Improved regex that avoids matching citations within URLs or markdown links
 * @param text The text containing citations like [1] or [1, 2, 3]
 * @param sources Array of sources to cite
 * @returns Object containing processed text and sources used
 */
export function processSourceCitations<
  T extends { url?: string | null; title?: string | null },
>(text: string, sources: T[]): { processedText: string; usedSources: T[] } {
  const normalizedText = normalizeAsciiCitations(text);
  let processedText = normalizedText;
  const sourcesUsedInGeneration: T[] = [];

  const citationRegex = /(?<!\]\([^\)]*)\[(\d+(?:,\s*\d+)*)\](?!\([^\)]*\))/g;

  processedText = normalizedText.replace(citationRegex, (match, numbers) => {
    const sourceNumbers = numbers
      .split(",")
      .map((n: string) => parseInt(n.trim()));

    const citedSources = sourceNumbers
      .map((num: number) => sources[num - 1])
      .filter(Boolean);

    if (citedSources.length === 0) return match;

    citedSources.forEach((source: T) => {
      if (
        source.url &&
        !sourcesUsedInGeneration.find((s) => s.url === source.url)
      ) {
        sourcesUsedInGeneration.push(source);
      }
    });

    const firstSource = citedSources[0];
    if (!firstSource.url) return match;

    const allUrls = citedSources
      .map((s: T) => s.url || "")
      .filter(Boolean)
      .join("|");
    const allTitles = citedSources
      .map((s: T) => (s.title || "").replace(/\|/g, ""))
      .join("|");

    try {
      const urlWithData = `${firstSource.url}#urls=${encodeURIComponent(allUrls)}&titles=${encodeURIComponent(allTitles)}`;
      return `[${numbers}](${urlWithData})`;
    } catch (error) {
      console.error("Error encoding citation data:", error);
      // Fallback to simple link
      return `[${numbers}](${firstSource.url})`;
    }
  });

  return { processedText, usedSources: sourcesUsedInGeneration };
}
